<template>
  <v-container fluid>
    <v-snackbar v-model="snackbar" :color="sbColor">
      {{ sbMessage }}
      <template #actions><v-btn @click="snackbar = false">Fechar</v-btn></template>
    </v-snackbar>

    <v-tabs v-model="tab" color="primary" class="mb-4">
      <v-tab value="usuarios">Usuários</v-tab>
      <v-tab value="alunos">Alunos</v-tab>
      <v-tab value="disciplinas">Disciplinas</v-tab>
      <v-tab value="turmas">Turmas</v-tab>
      <v-tab value="periodos">Períodos em curso</v-tab>
      <v-tab value="vinculos">Vínculos</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="usuarios">
        <v-card title="Professores e Coordenadores" prepend-icon="mdi-account-cog">
          <v-card-text>
            <v-form ref="usuarioFormRef">
              <v-row dense>
                <v-col cols="12" md="3"><v-select v-model="usuarioForm.tipo_usuario" label="Tipo" :items="tiposUsuario" item-title="label" item-value="value" variant="outlined" /></v-col>
                <v-col cols="12" md="5"><v-text-field v-model="usuarioForm.nome" label="Nome" variant="outlined" :rules="requiredRule" /></v-col>
                <v-col cols="12" md="4"><v-text-field v-model="usuarioForm.email" label="E-mail" variant="outlined" :rules="emailRule" /></v-col>
                <v-col cols="12" md="3"><v-select v-model="usuarioForm.genero" label="Gênero" :items="generos" item-title="label" item-value="value" variant="outlined" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="usuarioForm.cpf" label="CPF" variant="outlined" :rules="cpfRule" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="usuarioForm.telefone" label="Telefone" variant="outlined" :rules="telefoneRule" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="usuarioForm.password" label="Senha inicial" type="password" variant="outlined" :rules="passwordRuleUsuario" /></v-col>
              </v-row>
            </v-form>
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveUsuario">{{ usuarioForm.id ? "Atualizar" : "Incluir" }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetUsuario">Limpar</v-btn>

            <v-data-table class="table elevation-1 mt-5" :headers="usuarioHeaders" :items="usuarios" :loading="loading">
              <template #item.is_active="{ item }">{{ item.columns.is_active ? "Ativo" : "Inativo" }}</template>
              <template #item.actions="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="editUsuario(item.raw)" />
                <v-btn icon="mdi-delete" size="small" color="error" variant="text" @click="deleteUsuario(item.raw.id)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="alunos">
        <v-card title="Alunos e Matrículas" prepend-icon="mdi-account-school">
          <v-card-text>
            <v-alert class="mb-4" type="info" variant="tonal" density="compact">
              O aluno recebe um convite por e-mail para criar sua senha de primeiro acesso.
            </v-alert>
            <v-form ref="alunoFormRef">
              <v-row dense>
                <v-col cols="12" md="4"><v-text-field v-model="alunoForm.nome" label="Nome completo" variant="outlined" :rules="requiredRule" /></v-col>
                <v-col cols="12" md="3"><v-text-field v-model="alunoForm.email" label="E-mail" variant="outlined" :rules="emailRule" /></v-col>
                <v-col cols="12" md="2"><v-text-field v-model="alunoForm.ra" label="RA" variant="outlined" :rules="requiredRule" /></v-col>
                <v-col cols="12" md="2">
                  <v-select v-model="alunoForm.turma_id" label="Turma" :items="turmasAtivas" item-title="codigo" item-value="id" variant="outlined" :rules="requiredRule" />
                </v-col>
                <v-col cols="12" md="1"><v-switch v-model="alunoForm.is_active" label="Ativo" color="primary" /></v-col>
              </v-row>
            </v-form>
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveAluno">{{ alunoForm.aluno_id ? "Atualizar" : "Cadastrar e convidar" }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetAluno">Limpar</v-btn>

            <v-data-table class="table elevation-1 mt-5" :headers="alunoHeaders" :items="alunos" :loading="loading">
              <template #item.periodo_atual="{ item }">{{ formatPeriodoAtual(item.raw.periodo_atual) }}</template>
              <template #item.is_active="{ item }">{{ item.columns.is_active ? "Ativo" : "Inativo" }}</template>
              <template #item.actions="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="editAluno(item.raw)" />
                <v-btn icon="mdi-email-fast-outline" size="small" variant="text" color="primary" @click="inviteAluno(item.raw.aluno_id)" />
                <v-btn icon="mdi-delete" size="small" color="error" variant="text" @click="deleteAluno(item.raw.aluno_id)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="disciplinas">
        <v-card title="Disciplinas" prepend-icon="mdi-book-open-page-variant">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="5"><v-text-field v-model="disciplinaForm.nome" label="Nome da disciplina" variant="outlined" /></v-col>
              <v-col cols="12" md="5">
                <v-select
                  v-model="disciplinaForm.periodo_ids"
                  label="Períodos curriculares"
                  :items="periodosCurriculares"
                  item-title="label"
                  item-value="id"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="2"><v-switch v-model="disciplinaForm.ativa" label="Ativa" color="primary" /></v-col>
            </v-row>
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveDisciplina">{{ disciplinaForm.id ? "Atualizar" : "Criar" }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetDisciplina">Limpar</v-btn>
            <v-data-table
              class="table elevation-1 mt-5"
              :headers="disciplinaHeaders"
              :items="disciplinas"
              :loading="loading"
              :custom-key-sort="{ periodos: sortPeriodos }"
            >
              <template #item.periodos="{ item }">{{ formatPeriodos(item.raw.periodos) }}</template>
              <template #item.ativa="{ item }">{{ item.columns.ativa ? "Ativa" : "Inativa" }}</template>
              <template #item.actions="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="editDisciplina(item.raw)" />
                <v-btn icon="mdi-delete" size="small" color="error" variant="text" @click="deleteDisciplina(item.raw.id)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="turmas">
        <v-card title="Turmas" prepend-icon="mdi-google-classroom">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="3"><v-text-field v-model="turmaForm.codigo" label="Código" variant="outlined" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="turmaForm.nome" label="Nome" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-switch v-model="turmaForm.ativa" label="Ativa" color="primary" /></v-col>
            </v-row>
            <v-textarea v-model="turmaForm.descricao" label="Descrição" rows="2" variant="outlined" />
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveTurma">{{ turmaForm.id ? "Atualizar" : "Criar" }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetTurma">Limpar</v-btn>
            <v-data-table class="table elevation-1 mt-5" :headers="turmaHeaders" :items="turmas" :loading="loading">
              <template #item.ativa="{ item }">{{ item.columns.ativa ? "Ativa" : "Inativa" }}</template>
              <template #item.actions="{ item }"><v-btn icon="mdi-pencil" size="small" variant="text" @click="editTurma(item.raw)" /></template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="periodos">
        <v-card title="Período Atual das Turmas" prepend-icon="mdi-calendar-school">
          <v-card-text>
            <v-alert class="mb-4" type="info" variant="tonal" density="compact">
              Informe em qual período cada turma está cursando. As disciplinas correspondentes aparecerão automaticamente na aba Vínculos.
            </v-alert>
            <v-row dense>
              <v-col cols="12" md="2"><v-text-field v-model.number="turmaPeriodoForm.ano" label="Ano" type="number" variant="outlined" /></v-col>
              <v-col cols="12" md="2"><v-select v-model="turmaPeriodoForm.semestre" label="Semestre" :items="semestres" item-title="label" item-value="value" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="turmaPeriodoForm.turma" label="Turma" :items="turmasAtivas" item-title="codigo" item-value="id" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="turmaPeriodoForm.periodo_curricular" label="Modalidade e período da turma" :items="periodosCurriculares" item-title="label" item-value="id" variant="outlined" /></v-col>
              <v-col cols="12" md="2"><v-switch v-model="turmaPeriodoForm.ativa" label="Em curso" color="primary" /></v-col>
            </v-row>
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveTurmaPeriodo">{{ turmaPeriodoForm.id ? "Atualizar" : "Salvar período da turma" }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetTurmaPeriodo">Limpar</v-btn>
            <v-data-table class="table elevation-1 mt-4" :headers="turmaPeriodoHeaders" :items="turmasPeriodos" :loading="loading">
              <template #item.periodo_letivo_codigo="{ item }">{{ item.raw.periodo_letivo_ano }} / {{ item.raw.periodo_letivo_semestre }}º semestre</template>
              <template #item.ativa="{ item }">{{ item.columns.ativa ? "Em curso" : "Encerrado" }}</template>
              <template #item.actions="{ item }"><v-btn icon="mdi-pencil" size="small" variant="text" @click="editTurmaPeriodo(item.raw)" /></template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <v-window-item value="vinculos">
        <v-card title="Professor, Disciplina e Turma" prepend-icon="mdi-link-variant">
          <v-card-text>
            <v-alert v-if="!loading && !ofertas.length" class="mb-4" type="info" variant="tonal" density="compact">
              Nenhuma disciplina está ofertada para uma turma em período ativo. Confira a grade da disciplina e o período em curso da turma.
            </v-alert>
            <v-row dense>
              <v-col cols="12" md="3"><v-select v-model="vinculoForm.professor" label="Professor" :items="professoresVinculo" item-title="label" item-value="id" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="vinculoForm.oferta_disciplina" label="Disciplina em oferta" :items="ofertasVinculo" item-title="label" item-value="id" variant="outlined" :disabled="vinculoForm.bulk" @update:model-value="selectOfertaVinculo" /></v-col>
              <v-col cols="12" md="2">
                <v-select v-if="vinculoForm.bulk" v-model="vinculoForm.turma" label="Turma em curso" :items="turmasComOferta" item-title="label" item-value="id" variant="outlined" @update:model-value="selectTurmaVinculo" />
                <v-text-field v-else :model-value="vinculoForm.turma_codigo" label="Turma em curso" variant="outlined" readonly />
              </v-col>
              <v-col cols="12" md="2"><v-text-field :model-value="periodoVinculoDisplay" label="Período em curso" variant="outlined" readonly /></v-col>
              <v-col cols="12" md="1"><v-checkbox v-model="vinculoForm.bulk" label="Todas" color="primary" @update:model-value="changeVinculoBulk" /></v-col>
              <v-col cols="12" md="1"><v-switch v-model="vinculoForm.ativa" label="Ativo" color="primary" /></v-col>
            </v-row>
            <v-btn color="primary" variant="flat" :loading="loading" @click="saveVinculo">{{ vinculoForm.bulk ? "Vincular todas" : (vinculoForm.id ? "Atualizar" : "Vincular") }}</v-btn>
            <v-btn class="ms-2" variant="text" @click="resetVinculo">Limpar</v-btn>
            <v-data-table class="table elevation-1 mt-5" :headers="vinculoHeaders" :items="vinculos" :loading="loading">
              <template #item.professor_nome="{ item }">{{ formatProfessorVinculo(item.raw) }}</template>
              <template #item.periodo_letivo="{ item }">{{ formatPeriodoVinculo(item.raw) }}</template>
              <template #item.ativa="{ item }">{{ item.columns.ativa ? "Ativo" : "Inativo" }}</template>
              <template #item.actions="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="editVinculo(item.raw)" />
                <v-btn icon="mdi-delete" size="small" color="error" variant="text" @click="deleteVinculo(item.raw.id)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script>
import { VDataTable } from "vuetify/labs/VDataTable";
import UserDataService from "@/assets/js/services/UserDataService";
import { formatServerError } from "@/assets/js/utils/apiErrors";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const blankUsuario = () => ({ id: null, tipo_usuario: "professor", nome: "", email: "", genero: "ni", cpf: "", telefone: "", password: "", is_active: true });
const blankAluno = () => ({ aluno_id: null, nome: "", email: "", ra: "", turma_id: null, is_active: true });
const blankDisciplina = () => ({ id: null, nome: "", ativa: true, periodo_ids: [] });
const blankTurma = () => ({ id: null, codigo: "", nome: "", descricao: "", ativa: true });
const blankTurmaPeriodo = () => ({ id: null, ano: new Date().getFullYear(), semestre: 1, turma: null, periodo_curricular: null, ativa: true });
const blankVinculo = () => ({ id: null, professor: null, oferta_disciplina: null, disciplina_id: null, turma: null, turma_codigo: "", periodo_curricular_nome: "", periodo_letivo: "", ativa: true, bulk: false });

export default {
  name: "AdminSistema",
  components: { VDataTable },
  data: () => ({
    tab: "usuarios",
    loading: false,
    snackbar: false,
    sbColor: "success",
    sbMessage: "",
    usuarios: [],
    alunos: [],
    disciplinas: [],
    periodosCurriculares: [],
    turmasPeriodos: [],
    ofertas: [],
    turmas: [],
    vinculos: [],
    usuarioForm: blankUsuario(),
    alunoForm: blankAluno(),
    disciplinaForm: blankDisciplina(),
    turmaForm: blankTurma(),
    turmaPeriodoForm: blankTurmaPeriodo(),
    vinculoForm: blankVinculo(),
    tiposUsuario: [
      { value: "professor", label: "Professor" },
      { value: "coordenador", label: "Coordenador" },
    ],
    requiredRule: [(v) => !!(v && String(v).trim()) || "Campo obrigatório."],
    emailRule: [
      (v) => !!(v && String(v).trim()) || "Campo obrigatório.",
      (v) => /.+@.+\..+/.test(v || "") || "E-mail inválido.",
    ],
    cpfRule: [(v) => !v || /^\d{11}$/.test(onlyDigits(v)) || "CPF deve conter 11 dígitos numéricos."],
    telefoneRule: [(v) => !v || /^\d{10,11}$/.test(onlyDigits(v)) || "Telefone inválido."],
    generos: [
      { value: "m", label: "Masculino" },
      { value: "f", label: "Feminino" },
      { value: "ni", label: "Não desejo informar" },
    ],
    semestres: [
      { value: 1, label: "1º semestre" },
      { value: 2, label: "2º semestre" },
    ],
    usuarioHeaders: [
      { title: "Tipo", key: "tipo_usuario" }, { title: "Nome", key: "nome" },
      { title: "E-mail", key: "email" }, { title: "Status", key: "is_active" },
      { title: "", key: "actions", sortable: false },
    ],
    alunoHeaders: [
      { title: "Nome", key: "nome" }, { title: "RA", key: "ra" },
      { title: "E-mail", key: "email" }, { title: "Turma", key: "turma_codigo" },
      { title: "Período atual", key: "periodo_atual" }, { title: "Status", key: "is_active" },
      { title: "", key: "actions", sortable: false },
    ],
    disciplinaHeaders: [
      { title: "Disciplina", key: "nome" }, { title: "Período(s)", key: "periodos" }, { title: "Status", key: "ativa" },
      { title: "", key: "actions", sortable: false },
    ],
    turmaHeaders: [
      { title: "Código", key: "codigo" }, { title: "Nome", key: "nome" },
      { title: "Alunos", key: "alunos_count" }, { title: "Status", key: "ativa" },
      { title: "", key: "actions", sortable: false },
    ],
    turmaPeriodoHeaders: [
      { title: "Ano/Semestre", key: "periodo_letivo_codigo" }, { title: "Turma", key: "turma_codigo" },
      { title: "Período da turma", key: "periodo_curricular_nome" }, { title: "Status", key: "ativa" },
      { title: "", key: "actions", sortable: false },
    ],
    vinculoHeaders: [
      { title: "Professor", key: "professor_nome" }, { title: "Turma", key: "turma_codigo" },
      { title: "Disciplina", key: "disciplina" }, { title: "Período", key: "periodo_letivo" },
      { title: "Status", key: "ativa" }, { title: "", key: "actions", sortable: false },
    ],
  }),
  computed: {
    professores() { return this.usuarios.filter((u) => u.tipo_usuario === "professor" && u.is_active); },
    professoresVinculo() {
      return this.professores.map((professor) => ({
        ...professor,
        label: `${professor.nome} (${professor.email})`,
      }));
    },
    disciplinasAtivas() { return this.disciplinas.filter((d) => d.ativa); },
    turmasAtivas() { return this.turmas.filter((t) => t.ativa); },
    ofertasVinculo() {
      return this.ofertas.map((oferta) => ({
        ...oferta,
        label: `${oferta.disciplina_nome} - ${oferta.periodo_curricular_nome} - ${oferta.turma_codigo}`,
      }));
    },
    turmasComOferta() {
      const turmas = new Map();
      this.ofertas.forEach((oferta) => {
        if (!turmas.has(oferta.turma_id)) {
          turmas.set(oferta.turma_id, {
            id: oferta.turma_id,
            label: `${oferta.turma_codigo} - ${oferta.periodo_curricular_nome}`,
            periodo_curricular_nome: oferta.periodo_curricular_nome,
            periodo_letivo: oferta.periodo_letivo,
            turma_codigo: oferta.turma_codigo,
          });
        }
      });
      return Array.from(turmas.values());
    },
    periodoVinculoDisplay() {
      return this.vinculoForm.periodo_curricular_nome || "";
    },
    passwordRuleUsuario() {
      return this.usuarioForm.id
        ? []
        : [(v) => !!(v && String(v).trim()) || "Informe uma senha inicial."];
    },
  },
  mounted() { this.loadAll(); },
  methods: {
    notify(color, message) { this.sbColor = color; this.sbMessage = message; this.snackbar = true; },
    async loadAll() {
      this.loading = true;
      try {
        const [usuarios, alunos, disciplinas, periodosCurriculares, turmasPeriodos, ofertas, turmas, vinculos] = await Promise.all([
          UserDataService.sistemaUsuarios(), UserDataService.sistemaAlunos(), UserDataService.sistemaDisciplinas(),
          UserDataService.sistemaPeriodosCurriculares(), UserDataService.sistemaTurmasPeriodos(),
          UserDataService.sistemaOfertas(), UserDataService.listTurmas(),
          UserDataService.sistemaVinculos(),
        ]);
        this.usuarios = usuarios.data || [];
        this.alunos = alunos.data || [];
        this.disciplinas = disciplinas.data || [];
        this.periodosCurriculares = periodosCurriculares.data || [];
        this.turmasPeriodos = turmasPeriodos.data || [];
        this.ofertas = ofertas.data || [];
        this.turmas = turmas.data || [];
        this.vinculos = vinculos.data || [];
      } catch (error) { this.notify("error", error.response?.data?.error || error.message); }
      finally { this.loading = false; }
    },
    resetUsuario() { this.usuarioForm = blankUsuario(); },
    editUsuario(item) { this.usuarioForm = { ...blankUsuario(), ...item, password: "" }; },
    async saveUsuario() {
      const { valid } = await this.$refs.usuarioFormRef.validate();
      if (!valid) return;
      this.loading = true;
      try {
        const payload = { ...this.usuarioForm, cpf: onlyDigits(this.usuarioForm.cpf), telefone: onlyDigits(this.usuarioForm.telefone) };
        if (!payload.password) delete payload.password;
        if (payload.id) await UserDataService.sistemaUsuarioUpdate(payload);
        else await UserDataService.sistemaUsuarioCreate(payload);
        this.notify("success", "Usuário salvo.");
        this.resetUsuario(); await this.loadAll();
      } catch (error) { this.notify("error", formatServerError(error.response?.data, error.message)); }
      finally { this.loading = false; }
    },
    async deleteUsuario(id) { await UserDataService.sistemaUsuarioDelete(id); this.notify("success", "Usuário desativado."); await this.loadAll(); },
    resetAluno() { this.alunoForm = blankAluno(); },
    editAluno(item) {
      this.alunoForm = {
        ...blankAluno(),
        aluno_id: item.aluno_id,
        nome: item.nome,
        email: item.email,
        ra: item.ra,
        turma_id: item.turma_id,
        is_active: item.is_active,
      };
    },
    formatPeriodoAtual(periodo) {
      return periodo ? `${periodo.nome} (${periodo.periodo_letivo})` : "Período não aberto";
    },
    async saveAluno() {
      const { valid } = await this.$refs.alunoFormRef.validate();
      if (!valid) return;
      this.loading = true;
      try {
        if (this.alunoForm.aluno_id) await UserDataService.sistemaAlunoUpdate(this.alunoForm);
        else await UserDataService.sistemaAlunoCreate(this.alunoForm);
        this.notify("success", this.alunoForm.aluno_id ? "Aluno atualizado." : "Aluno cadastrado e convite enviado.");
        this.resetAluno(); await this.loadAll();
      } catch (error) { this.notify("error", formatServerError(error.response?.data, error.message)); }
      finally { this.loading = false; }
    },
    async inviteAluno(id) {
      await UserDataService.sistemaAlunoInvite(id);
      this.notify("success", "Convite de primeiro acesso enviado.");
    },
    async deleteAluno(id) {
      await UserDataService.sistemaAlunoDelete(id);
      this.notify("success", "Aluno desativado."); await this.loadAll();
    },
    resetDisciplina() { this.disciplinaForm = blankDisciplina(); },
    editDisciplina(item) {
      this.disciplinaForm = {
        ...blankDisciplina(),
        ...item,
        periodo_ids: (item.periodos || []).map((periodo) => periodo.id),
      };
    },
    formatPeriodos(periodos) {
      return (periodos || []).map((periodo) => periodo.label || periodo.nome).join(", ") || "Sem período";
    },
    sortPeriodos(left, right) {
      const leftNumbers = (left || [])
        .map((periodo) => `${periodo.modalidade || ""}-${String(periodo.numero).padStart(2, "0")}`)
        .sort();
      const rightNumbers = (right || [])
        .map((periodo) => `${periodo.modalidade || ""}-${String(periodo.numero).padStart(2, "0")}`)
        .sort();
      const length = Math.max(leftNumbers.length, rightNumbers.length);

      for (let index = 0; index < length; index += 1) {
        if (leftNumbers[index] === undefined) return 1;
        if (rightNumbers[index] === undefined) return -1;
        if (leftNumbers[index] !== rightNumbers[index]) return leftNumbers[index].localeCompare(rightNumbers[index]);
      }

      return 0;
    },
    async saveDisciplina() {
      if (this.disciplinaForm.id) await UserDataService.sistemaDisciplinaUpdate(this.disciplinaForm);
      else await UserDataService.sistemaDisciplinaCreate(this.disciplinaForm);
      this.notify("success", "Disciplina salva."); this.resetDisciplina(); await this.loadAll();
    },
    async deleteDisciplina(id) { await UserDataService.sistemaDisciplinaDelete(id); this.notify("success", "Disciplina desativada."); await this.loadAll(); },
    resetTurma() { this.turmaForm = blankTurma(); },
    editTurma(item) { this.turmaForm = { ...blankTurma(), ...item }; },
    async saveTurma() { await UserDataService.sistemaTurmaSave(this.turmaForm); this.notify("success", "Turma salva."); this.resetTurma(); await this.loadAll(); },
    resetTurmaPeriodo() { this.turmaPeriodoForm = blankTurmaPeriodo(); },
    editTurmaPeriodo(item) {
      this.turmaPeriodoForm = {
        ...blankTurmaPeriodo(),
        ...item,
        ano: item.periodo_letivo_ano,
        semestre: item.periodo_letivo_semestre,
        periodo_letivo: null,
      };
    },
    async saveTurmaPeriodo() {
      try {
        await UserDataService.sistemaTurmaPeriodoSave(this.turmaPeriodoForm);
        this.notify("success", "Turma vinculada ao período em curso; ofertas atualizadas.");
        this.resetTurmaPeriodo(); await this.loadAll();
      } catch (error) { this.notify("error", JSON.stringify(error.response?.data || error.message)); }
    },
    resetVinculo() { this.vinculoForm = blankVinculo(); },
    editVinculo(item) {
      this.vinculoForm = { ...blankVinculo(), ...item };
      this.selectOfertaVinculo(item.oferta_disciplina);
    },
    selectOfertaVinculo(ofertaId) {
      const oferta = this.ofertas.find((item) => item.id === ofertaId);
      if (!oferta) {
        this.vinculoForm.turma = null;
        this.vinculoForm.turma_codigo = "";
        this.vinculoForm.periodo_curricular_nome = "";
        this.vinculoForm.periodo_letivo = "";
        return;
      }
      this.vinculoForm.turma = oferta.turma_id;
      this.vinculoForm.turma_codigo = oferta.turma_codigo;
      this.vinculoForm.periodo_curricular_nome = oferta.periodo_curricular_nome;
      this.vinculoForm.periodo_letivo = oferta.periodo_letivo;
    },
    selectTurmaVinculo(turmaId) {
      const turma = this.turmasComOferta.find((item) => item.id === turmaId);
      this.vinculoForm.turma_codigo = turma?.turma_codigo || "";
      this.vinculoForm.periodo_curricular_nome = turma?.periodo_curricular_nome || "";
      this.vinculoForm.periodo_letivo = turma?.periodo_letivo || "";
    },
    formatPeriodoVinculo(item) {
      return item.periodo_curricular_nome || "-";
    },
    formatProfessorVinculo(item) {
      if (!item.professor_email) return item.professor_nome;
      return `${item.professor_nome} (${item.professor_email})`;
    },
    changeVinculoBulk(bulk) {
      this.vinculoForm.oferta_disciplina = null;
      this.vinculoForm.turma = null;
      this.vinculoForm.turma_codigo = "";
      this.vinculoForm.periodo_curricular_nome = "";
      this.vinculoForm.periodo_letivo = "";
      if (!bulk) this.vinculoForm.id = null;
    },
    async saveVinculo() {
      const payload = { ...this.vinculoForm };
      if (payload.bulk) {
        payload.id = null;
        payload.oferta_disciplina = null;
      }
      if (payload.id) await UserDataService.sistemaVinculoUpdate(payload);
      else await UserDataService.sistemaVinculoCreate(payload);
      this.notify("success", "Vínculo salvo."); this.resetVinculo(); await this.loadAll();
    },
    async deleteVinculo(id) { await UserDataService.sistemaVinculoDelete(id); this.notify("success", "Vínculo desativado."); await this.loadAll(); },
  },
};
</script>
